import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { supabase } from './lib/supabase';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { Dashboard } from './components/dashboard/Dashboard';
import { ContactList } from './components/contacts/ContactList';
import { ContactDetail } from './components/contacts/ContactDetail';
import { OrganizationList } from './components/organizations/OrganizationList';
import { OrganizationDetail } from './components/organizations/OrganizationDetail';
import { InitiativeList } from './components/initiatives/InitiativeList';
import { InitiativeDetail } from './components/initiatives/InitiativeDetail';
import { ActivityList } from './components/activity/ActivityList';
import { Graph } from './components/graph/Graph';
import { Search } from './components/search/Search';
import { TaskList } from './components/tasks/TaskList';
import { Board } from './components/board/Board';
import { Settings } from './components/settings/Settings';
import { Homepage } from './components/Homepage';
import { ContactSales } from './components/ContactSales';
import { InvestorsLanding } from './components/InvestorsLanding';
import { ExecutivesLanding } from './components/ExecutivesLanding';
import { Knowledge } from './components/Knowledge';
import { KnowledgeGraph } from './components/knowledge/KnowledgeGraph';
import { KnowledgeArticle } from './components/knowledge/KnowledgeArticle';
import { ReportsPage } from './components/reports/ReportsPage';
// import { FrontChat } from './components/FrontChat';
import LearningSystem from './components/learning/LearningSystem';
import { EmailPage } from './components/email/EmailPage';
import { Profile } from './components/profile/Profile';

// Protected route wrapper
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <SessionContextProvider supabaseClient={supabase}>
      <ThemeProvider>
        <AuthProvider>
          <SettingsProvider>
            <Router>
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/contact-sales" element={<ContactSales />} />
              <Route path="/investors" element={<InvestorsLanding />} />
              <Route path="/executives" element={<ExecutivesLanding />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contacts"
                element={
                  <PrivateRoute>
                    <ContactList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contacts/:id"
                element={
                  <PrivateRoute>
                    <ContactDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/email"
                element={
                  <PrivateRoute>
                    <EmailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <PrivateRoute>
                    <OrganizationList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/organizations/:id"
                element={
                  <PrivateRoute>
                    <OrganizationDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/initiatives"
                element={
                  <PrivateRoute>
                    <InitiativeList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/initiatives/:id"
                element={
                  <PrivateRoute>
                    <InitiativeDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/activity"
                element={
                  <PrivateRoute>
                    <ActivityList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <PrivateRoute>
                    <TaskList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/graph"
                element={
                  <PrivateRoute>
                    <Graph />
                  </PrivateRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <PrivateRoute>
                    <Search />
                  </PrivateRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <PrivateRoute>
                    <Board />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Settings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/knowledge"
                element={
                  <PrivateRoute>
                    <Knowledge />
                  </PrivateRoute>
                }
              />
              <Route
                path="/knowledge-graph"
                element={
                  <PrivateRoute>
                    <KnowledgeGraph />
                  </PrivateRoute>
                }
              />
              <Route
                path="/knowledge/:id"
                element={
                  <PrivateRoute>
                    <KnowledgeArticle />
                  </PrivateRoute>
                }
              />
              <Route
                path="/learning"
                element={
                  <PrivateRoute>
                    <LearningSystem />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute>
                    <ReportsPage />
                  </PrivateRoute>
                }
              />
            </Routes>
            {/* <FrontChat chatId="cf7bda6535083d53973c53bed540911a" /> */}
          </Router>
        </SettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </SessionContextProvider>
  );
}

export default App;
